import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import MarkdownInput from "@/components/kg/Markdown";
import { ResearchPanel } from "@/components/ai/ResearchPanel";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "markdown", label: "Rich text (Markdown)" },
  { value: "list", label: "List (one per line)" },
  { value: "enum", label: "Dropdown (fixed options)" },
];

// ---------- Dynamic node form ----------
export const NodeDialog = ({ open, onOpenChange, nodeType, editing, onSaved, prefill }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [researchOpen, setResearchOpen] = useState(false);
  const [insertText, setInsertText] = useState(null); // text pending a target-field choice
  const [fillProposal, setFillProposal] = useState(null); // { values:{key:str}, checks:{key:bool} }
  const [filling, setFilling] = useState(false);

  const nameKey = nodeType.name_field || "name";
  const textFields = nodeType.fields.filter((f) => ["textarea", "markdown", "text"].includes(f.type));
  const fieldByKey = Object.fromEntries(nodeType.fields.map((f) => [f.key, f]));

  const insertIntoField = (fieldKey, label) => {
    const existing = form[fieldKey] || "";
    setForm({ ...form, [fieldKey]: existing ? `${existing}\n\n${insertText}` : insertText });
    toast.success(`Inserted into ${label}`);
    setInsertText(null);
  };

  const runAutoFill = async (text) => {
    setResearchOpen(false);
    setFilling(true);
    try {
      const res = await api.post("/ai/fill-fields", { type: nodeType.key, name: form[nameKey] || "", text });
      const raw = res.data.fields || {};
      const values = {}; const checks = {};
      Object.entries(raw).forEach(([k, v]) => {
        values[k] = Array.isArray(v) ? v.join("\n") : String(v);
        checks[k] = !((form[k] || "").trim()); // default-apply only where the form is empty
      });
      setFillProposal({ values, checks });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't extract field values");
    } finally { setFilling(false); }
  };

  const applyAutoFill = () => {
    const next = { ...form };
    Object.entries(fillProposal.values).forEach(([k, v]) => {
      if (fillProposal.checks[k]) next[k] = v;
    });
    setForm(next);
    const n = Object.values(fillProposal.checks).filter(Boolean).length;
    toast.success(`Filled ${n} field${n === 1 ? "" : "s"}`);
    setFillProposal(null);
  };

  useEffect(() => {
    if (!open) return;
    const init = {};
    nodeType.fields.forEach((f) => {
      const val = editing?.data?.[f.key];
      init[f.key] = f.type === "list" ? (Array.isArray(val) ? val.join("\n") : "") : (val || "");
    });
    if (!editing && prefill?.name) init[nameKey] = prefill.name;
    if (!editing && prefill?.fields) {
      Object.entries(prefill.fields).forEach(([k, v]) => {
        if (nodeType.fields.some((f) => f.key === k)) init[k] = v;
      });
    }
    setForm(init);
  }, [open, editing, nodeType, prefill]);

  const save = async (submit) => {
    if (!form[nameKey]?.trim()) { toast.error(`${nameKey} is required`); return; }
    setSaving(true);
    const data = {};
    nodeType.fields.forEach((f) => {
      const v = form[f.key];
      if (f.type === "list") {
        data[f.key] = (v || "").split("\n").map((x) => x.trim()).filter(Boolean);
      } else if (v && v.trim()) {
        data[f.key] = v.trim();
      }
    });
    try {
      if (editing) {
        await api.put(`/kg/nodes/${editing.id}`, { data });
        toast.success(editing.status === "published" ? "Edit submitted for review" : "Draft saved");
      } else {
        await api.post("/kg/nodes", { type: nodeType.key, data, submit: !!submit });
        toast.success(submit ? "Submitted for review" : "Draft saved");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="node-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="font-serif text-2xl">
              {editing ? "Edit" : "New"} {nodeType.label}
            </DialogTitle>
            <Button type="button" size="sm" onClick={() => setResearchOpen(true)} disabled={filling}
              className="bg-[#ffb020] hover:bg-[#e69a10] text-[#14140f] shrink-0" data-testid="node-research-btn">
              {filling ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              {filling ? "Extracting…" : "Research with AI"}
            </Button>
          </div>
          <DialogDescription>Fill in the fields for this {nodeType.label.toLowerCase()} entity.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {nodeType.fields.map((f) => (
            <div key={f.key}>
              <Label>{f.label}{f.required && <span className="text-primary"> *</span>}</Label>
              {f.type === "textarea" && (
                <Textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1 min-h-[90px]" data-testid={`field-${f.key}`} />
              )}
              {f.type === "markdown" && (
                <MarkdownInput value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })}
                  testId={`field-${f.key}`} />
              )}
              {f.type === "list" && (
                <>
                  <Textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="mt-1 min-h-[70px]" placeholder="One value per line" data-testid={`field-${f.key}`} />
                  <p className="text-[11px] text-muted-foreground mt-1">One value per line</p>
                </>
              )}
              {f.type === "enum" && (
                <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                  <SelectTrigger className="mt-1" data-testid={`field-${f.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {f.type === "text" && (
                <Input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1" data-testid={`field-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {editing ? (
            <Button onClick={() => save(false)} disabled={saving} data-testid="save-node-btn" className="bg-primary hover:bg-primary/90">
              {saving ? "Saving…" : editing.status === "published" ? "Submit edit for review" : "Save changes"}
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn">
                {saving ? "Saving…" : "Save draft"}
              </Button>
              <Button onClick={() => save(true)} disabled={saving} data-testid="save-node-btn" className="bg-primary hover:bg-primary/90">
                Submit for review
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <ResearchPanel
      open={researchOpen}
      onOpenChange={setResearchOpen}
      contextLabel={form[nameKey] || nodeType.label}
      onInsertEntity={(text) => { setInsertText(text); setResearchOpen(false); }}
      onFillFields={(text) => runAutoFill(text)}
      enableExtract
      enableSaveNote
      presetPrompt={form[nameKey] ? `Tell me about ${form[nameKey]} in ancient Indian philosophy, with sources.` : undefined}
    />

    <Dialog open={fillProposal !== null} onOpenChange={(o) => !o && setFillProposal(null)}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="autofill-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Auto-fill fields</DialogTitle>
          <DialogDescription>Review the values the AI drew from your research. Uncheck any you don't want (fields you've already filled are unchecked by default).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {fillProposal && Object.entries(fillProposal.values).map(([k, v]) => (
            <div key={k} className={`rounded-md border p-3 ${fillProposal.checks[k] ? "border-primary/40 bg-secondary/30" : "border-border opacity-70"}`} data-testid={`autofill-field-${k}`}>
              <label className="flex items-center gap-2 mb-1.5">
                <input type="checkbox" checked={fillProposal.checks[k]} className="accent-[#ffb020]"
                  onChange={(e) => setFillProposal((p) => ({ ...p, checks: { ...p.checks, [k]: e.target.checked } }))}
                  data-testid={`autofill-check-${k}`} />
                <span className="text-sm font-semibold text-foreground">{fieldByKey[k]?.label || k}</span>
              </label>
              <Textarea value={v} onChange={(e) => setFillProposal((p) => ({ ...p, values: { ...p.values, [k]: e.target.value } }))}
                className="min-h-[56px] text-xs" data-testid={`autofill-value-${k}`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setFillProposal(null)}>Cancel</Button>
          <Button onClick={applyAutoFill} className="bg-primary hover:bg-primary/90" data-testid="autofill-apply-btn">Apply to form</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={insertText !== null} onOpenChange={(o) => !o && setInsertText(null)}>
      <DialogContent className="max-w-md" data-testid="insert-field-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Insert research into…</DialogTitle>
          <DialogDescription>Choose which field to append this research to.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1">
          {textFields.map((f) => (
            <Button key={f.key} variant="outline" className="w-full justify-start"
              onClick={() => insertIntoField(f.key, f.label)} data-testid={`insert-into-${f.key}`}>
              {f.label}
            </Button>
          ))}
          {textFields.length === 0 && <p className="text-sm text-muted-foreground">This entity type has no text fields to insert into.</p>}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

// ---------- Edge dialog ----------
export const EdgeDialog = ({ open, onOpenChange, edgeTypes, editing, onSaved }) => {
  const [typeKey, setTypeKey] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [props, setProps] = useState({});
  const [sourceNodes, setSourceNodes] = useState([]);
  const [targetNodes, setTargetNodes] = useState([]);
  const [saving, setSaving] = useState(false);
  const et = edgeTypes.find((e) => e.key === typeKey);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTypeKey(editing.type); setSourceId(editing.source_id);
      setTargetId(editing.target_id); setProps(editing.props || {});
    } else {
      setTypeKey(""); setSourceId(""); setTargetId(""); setProps({});
      setSourceNodes([]); setTargetNodes([]);
    }
  }, [open, editing]);

  useEffect(() => {
    if (!et || editing) return;
    setSourceId(""); setTargetId(""); setProps({});
    let cancelled = false;
    Promise.all([
      api.get(`/kg/nodes?type=${et.source}`),
      api.get(`/kg/nodes?type=${et.target}`),
    ]).then(([s, t]) => {
      if (cancelled) return;
      setSourceNodes(s.data); setTargetNodes(t.data);
    }).catch(() => {
      if (cancelled) return;
      setSourceNodes([]); setTargetNodes([]);
      toast.error("Could not load entities");
    });
    return () => { cancelled = true; };
  }, [typeKey]); // eslint-disable-line

  const save = async () => {
    if (!typeKey || !sourceId || !targetId) { toast.error("Choose relationship, source and target"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/kg/edges/${editing.id}`, { props });
      else await api.post("/kg/edges", { type: typeKey, source_id: sourceId, target_id: targetId, props });
      toast.success(editing ? "Relationship updated" : "Relationship created");
      onOpenChange(false); onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edge-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{editing ? "Edit relationship" : "New relationship"}</DialogTitle>
          <DialogDescription>Connect two entities with a typed relationship.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Relationship</Label>
            <Select value={typeKey} onValueChange={setTypeKey} disabled={!!editing}>
              <SelectTrigger className="mt-1" data-testid="edge-type-select"><SelectValue placeholder="Select relationship…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {edgeTypes.map((e) => (
                  <SelectItem key={e.key} value={e.key}>{e.label} ({e.source} → {e.target})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {editing ? (
            <div className="text-sm bg-secondary/40 rounded-md p-3 border border-border">
              <div><span className="text-muted-foreground">Source:</span> {editing.source_label}</div>
              <div><span className="text-muted-foreground">Target:</span> {editing.target_label}</div>
            </div>
          ) : et && (
            <>
              <div>
                <Label>Source ({et.source})</Label>
                <Select value={sourceId} onValueChange={setSourceId}>
                  <SelectTrigger className="mt-1" data-testid="edge-source-select"><SelectValue placeholder="Select source…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {sourceNodes.map((n) => <SelectItem key={n.id} value={n.id}>{n.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Target ({et.target})</Label>
                <Select value={targetId} onValueChange={setTargetId}>
                  <SelectTrigger className="mt-1" data-testid="edge-target-select"><SelectValue placeholder="Select target…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {targetNodes.map((n) => <SelectItem key={n.id} value={n.id}>{n.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          {et?.props?.map((p) => (
            <div key={p.key}>
              <Label>{p.label}</Label>
              {p.type === "enum" ? (
                <Select value={props[p.key] || ""} onValueChange={(v) => setProps({ ...props, [p.key]: v })}>
                  <SelectTrigger className="mt-1" data-testid={`edge-prop-${p.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{p.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Textarea value={props[p.key] || ""} onChange={(e) => setProps({ ...props, [p.key]: e.target.value })}
                  className="mt-1" data-testid={`edge-prop-${p.key}`} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-edge-btn" className="bg-primary hover:bg-primary/90">
            {saving ? "Saving…" : editing ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Manage custom fields (per entity type) ----------
export const FieldsDialog = ({ open, onOpenChange, nodeType, isAdmin, onChanged }) => {
  const [label, setLabel] = useState("");
  const [fieldType, setFieldType] = useState("text");
  const [options, setOptions] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setLabel(""); setFieldType("text"); setOptions(""); }
  }, [open]);

  const customFields = (nodeType?.fields || []).filter((f) => f.custom);

  const addField = async () => {
    if (!label.trim()) { toast.error("Field label is required"); return; }
    const body = { label: label.trim(), field_type: fieldType };
    if (fieldType === "enum") {
      body.options = options.split("\n").map((x) => x.trim()).filter(Boolean);
      if (body.options.length === 0) { toast.error("Add at least one option"); return; }
    }
    setSaving(true);
    try {
      await api.post(`/kg/schema/${nodeType.key}/fields`, body);
      toast.success("Field added");
      setLabel(""); setOptions(""); setFieldType("text");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add field");
    } finally { setSaving(false); }
  };

  const removeField = async (key) => {
    try {
      await api.delete(`/kg/schema/${nodeType.key}/fields/${key}`);
      toast.success("Field removed");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove field"); }
  };

  if (!nodeType) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="fields-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Custom fields · {nodeType.label}</DialogTitle>
          <DialogDescription>Add your own fields to every {nodeType.label.toLowerCase()} entry.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-1 max-h-52 overflow-y-auto">
          {customFields.length === 0 && <p className="text-sm text-muted-foreground">No custom fields yet.</p>}
          {customFields.map((f) => (
            <div key={f.key} className="flex items-center justify-between bg-secondary/40 rounded-md px-3 py-2" data-testid={`custom-field-${f.key}`}>
              <div>
                <span className="text-sm font-medium text-foreground">{f.label}</span>
                <span className="text-xs text-muted-foreground ml-2">{FIELD_TYPE_OPTIONS.find((o) => o.value === f.type)?.label || f.type}</span>
              </div>
              {isAdmin && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" title="Remove field"
                  onClick={() => removeField(f.key)} data-testid={`delete-field-${f.key}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>

        <div className="border-t border-border pt-4 space-y-3">
          <div>
            <Label>New field label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Iconography" className="mt-1" data-testid="new-field-label" />
          </div>
          <div>
            <Label>Field type</Label>
            <Select value={fieldType} onValueChange={setFieldType}>
              <SelectTrigger className="mt-1" data-testid="new-field-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FIELD_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {fieldType === "enum" && (
            <div>
              <Label>Options (one per line)</Label>
              <Textarea value={options} onChange={(e) => setOptions(e.target.value)} className="mt-1 min-h-[70px]" placeholder={"Option A\nOption B"} data-testid="new-field-options" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>
          <Button onClick={addField} disabled={saving} data-testid="add-field-btn" className="bg-primary hover:bg-primary/90">
            {saving ? "Adding…" : "Add field"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
